/**
 * Saving a copy, and bringing one back (DECISIONS D-020).
 *
 * CONNECTED: it reads the store and writes to it. The words are
 * `src/ui/state/transferView.ts` and the mechanism is `src/db/transfer.ts`;
 * this file is wiring and stages.
 *
 * ── Two things that are one thing ───────────────────────────────────────────
 * `SaveDeviceBackup` (everything on this device) and `RestoreFromFile` (read
 * one back) are the same feature seen from two places. `ExportBlock` — the
 * shared button, sizing and outcome copy — used to back a third,
 * `SaveSurveyCopy` (one survey, from its own screen); the final review's fix
 * wave (round 7) deleted it as dead code, reached by no screen (`RemoveSurvey`,
 * the component it stood beside, was the same — see git history for both).
 * `ExportBlock` keeps `scope: 'one' | 'device'` even with only `'device'`
 * ever passed now: it is what `exportView` (transferView.ts) still branches
 * on, and narrowing it is a separate, unasked-for cut.
 *
 * ── The file input is invisible on purpose ──────────────────────────────────
 * `<input type="file">` renders as a browser-chrome control this app cannot size
 * or colour, and D-011 puts a floor under both. So it is `display: none` and a
 * real 44px button clicks it. That also keeps it out of the legibility audit,
 * which skips what is not rendered — the button beside it is what the surveyor
 * actually touches and it is measured like everything else.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Card } from './Surfaces';
import {
  applyTransfer,
  estimateTransfer,
  planTransfer,
  readTransfer,
  type ImportPlan,
  type TransferBundle,
  type TransferEstimate,
  type TransferProblem,
} from '../../db/transfer';
import { saveSurveyBackup } from '../state/download';
import { useServices } from '../state/services';
import {
  exportView,
  importOutcomeMessage,
  importPlanView,
  problemView,
} from '../state/transferView';

/* ═════════════════════════════════ export ════════════════════════════════ */

interface ExportBlockProps {
  scope: 'one' | 'device';
  surveyIds: string[] | null;
}

/**
 * The shared export block. `surveyIds` is null while the device's list is still
 * being read; the block renders nothing rather than flashing a wrong size.
 */
function ExportBlock({ scope, surveyIds }: ExportBlockProps): ReactNode {
  const { store } = useServices();
  const [estimate, setEstimate] = useState<TransferEstimate | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const ids = surveyIds ?? [];
  const key = ids.join('|');

  useEffect(() => {
    if (surveyIds === null) return undefined;
    let cancelled = false;
    void estimateTransfer(store, surveyIds)
      .then((next) => {
        if (!cancelled) setEstimate(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // `key`, not the array: a new array identity on every render would
    // re-measure the whole device on every render of the screen around it.
  }, [store, key]);

  if (surveyIds === null || estimate === null) return null;
  // A button, not an essay (D-029 R5) — no surveys, no button, no card.
  if (estimate.surveys === 0) return null;

  const view = exportView({ scope, estimate });

  const save = (): void => {
    setBusy(true);
    setFailure(null);
    setSaid(null);
    void saveSurveyBackup(store, ids)
      .then((outcome) => {
        // A cancelled share sheet is not an event. Saying "saved" would be a
        // lie and saying "failed" would be an accusation, so it says nothing —
        // `saveSurveyBackup` already returns a null message for it.
        if (outcome.message) setSaid(outcome.message);
      })
      .catch((cause: unknown) => {
        setFailure(
          cause instanceof Error
            ? `The file could not be saved: ${cause.message}`
            : 'The file could not be saved.',
        );
      })
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      {/* The button is the whole story (D-029 R5) — the lead paragraphs and
          the size line were prose about the app. The ≥50MB warning survives
          because it is an operational fact the tap depends on. */}
      {view.sizeWarning ? (
        <p className="ruleline" data-legibility="helper">
          {view.sizeWarning}
        </p>
      ) : null}
      {view.photoNote ? (
        <p className="ruleline" data-legibility="helper">
          {view.photoNote}
        </p>
      ) : null}

      {estimate.surveys > 0 ? (
        <button
          type="button"
          className={scope === 'one' ? 'btn btn--ghost' : 'btn btn--primary'}
          data-testid={scope === 'one' ? 'export-survey' : 'export-device'}
          onClick={save}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Save backup'}
        </button>
      ) : null}

      <div aria-live="polite">
        {said ? (
          <p className="ruleline" data-legibility="helper">
            {said}
          </p>
        ) : null}
        {failure ? (
          <p className="ruleline" data-legibility="helper">
            {failure}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

/** Everything on this device, from the device screen. */
export function SaveDeviceBackup(): ReactNode {
  const { store } = useServices();
  const [ids, setIds] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = (): void => {
      void store
        .listSurveys()
        .then((records) => {
          if (!cancelled) setIds(records.map((record) => record.surveyId));
        })
        .catch(() => undefined);
    };
    refresh();
    const unsubscribe = store.subscribe(refresh);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [store]);

  return <ExportBlock scope="device" surveyIds={ids} />;
}

/* ═════════════════════════════════ import ════════════════════════════════ */

type ImportStage =
  | { at: 'idle' }
  | { at: 'reading' }
  | { at: 'refused'; problem: TransferProblem }
  | { at: 'choosing'; plan: ImportPlan; bundle: TransferBundle }
  | { at: 'writing' }
  | { at: 'done'; message: string };

/**
 * Read a backup file back onto this device.
 *
 * The stages exist because the surveyor is owed an answer at each of them: the
 * file was not one of ours; the file is fine and here is what is in it; two of
 * these are already on your phone and you decide; it is done and here is what
 * landed. Nothing is written until the last of those.
 */
export function RestoreFromFile(): ReactNode {
  const { store, sync } = useServices();
  const input = useRef<HTMLInputElement | null>(null);
  const [stage, setStage] = useState<ImportStage>({ at: 'idle' });
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());

  const pick = useCallback((): void => {
    input.current?.click();
  }, []);

  const onFile = useCallback(
    (file: File): void => {
      setStage({ at: 'reading' });
      void readTransfer(file)
        .then(async (result) => {
          if (!result.ok) {
            setStage({ at: 'refused', problem: result.problem });
            return;
          }
          const plan = await planTransfer(store, result.bundle);
          const start = new Set(
            plan.items
              .filter((item) => item.disposition === 'new')
              .map((item) => item.surveyId),
          );
          setChosen(start);
          setStage({ at: 'choosing', plan, bundle: result.bundle });
        })
        .catch((cause: unknown) => {
          setStage({
            at: 'refused',
            problem: {
              kind: 'damaged',
              detail: cause instanceof Error ? cause.message : 'the file could not be opened',
            },
          });
        });
    },
    [store],
  );

  const bring = useCallback(
    (bundle: TransferBundle): void => {
      setStage({ at: 'writing' });
      void applyTransfer(store, bundle, chosen)
        .then((outcome) => {
          setStage({ at: 'done', message: importOutcomeMessage(outcome) });
          // An imported survey owes the office exactly what a native one does,
          // so it is offered to the network immediately. Fire-and-forget: the
          // rows are already durable and a bad link must not hang the screen.
          if (outcome.imported.length > 0) void sync.drain().catch(() => undefined);
        })
        .catch((cause: unknown) => {
          setStage({
            at: 'refused',
            problem: {
              kind: 'damaged',
              detail: cause instanceof Error ? cause.message : 'the surveys could not be written',
            },
          });
        });
    },
    [store, sync, chosen],
  );

  const hidden = (
    <input
      ref={input}
      type="file"
      style={{ display: 'none' }}
      data-testid="import-file"
      onChange={(event) => {
        const file = event.target.files?.[0];
        // The value is cleared so picking the SAME file twice still fires — a
        // surveyor retrying after a refusal picks the same file by definition.
        event.target.value = '';
        if (file) onFile(file);
      }}
    />
  );

  if (stage.at === 'reading' || stage.at === 'writing') {
    return (
      <Card>
        {hidden}
        <p className="q" data-legibility="question">
          {stage.at === 'reading' ? 'Reading the file…' : 'Bringing the surveys in…'}
        </p>
        <p className="ruleline" data-legibility="helper">
          One moment. Nothing is changed on this phone until it is finished.
        </p>
      </Card>
    );
  }

  if (stage.at === 'refused') {
    const view = problemView(stage.problem);
    return (
      <Card>
        {hidden}
        <div role="status" aria-live="polite">
          <p className="q" data-legibility="question">
            {view.headline}
          </p>
          <p className="ruleline" data-legibility="helper">
            {view.detail}
          </p>
          <p className="ruleline" data-legibility="helper">
            {view.next}
          </p>
        </div>
        <button type="button" className="btn btn--ghost" onClick={pick}>
          Choose another file
        </button>
      </Card>
    );
  }

  if (stage.at === 'done') {
    return (
      <Card>
        {hidden}
        <div role="status" aria-live="polite">
          <p className="q" data-legibility="question">
            Done.
          </p>
          <p className="ruleline" data-legibility="helper">
            {stage.message}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => setStage({ at: 'idle' })}
        >
          Open another file
        </button>
      </Card>
    );
  }

  if (stage.at === 'choosing') {
    const view = importPlanView(stage.plan);
    return (
      <Card>
        {hidden}
        <p className="q" data-legibility="question">
          {view.headline}
        </p>
        {/* The instruction sits above the list it governs (D-011 item 6). */}
        <p className="leadin" data-legibility="helper">
          {view.lead}
        </p>

        <div role="group" aria-label="Surveys in this file">
          {view.items.map((item) => (
            <div key={item.surveyId}>
              <p className="ruleline" data-legibility="helper">
                {item.line}
              </p>
              {item.choosable ? (
                <button
                  type="button"
                  role="checkbox"
                  className="radio"
                  aria-checked={chosen.has(item.surveyId)}
                  onClick={() => {
                    setChosen((current) => {
                      const next = new Set(current);
                      if (next.has(item.surveyId)) next.delete(item.surveyId);
                      else next.add(item.surveyId);
                      return next;
                    });
                  }}
                >
                  <span className="radio__mark" aria-hidden="true" />
                  <span className="radio__text">{item.choiceLabel}</span>
                </button>
              ) : null}
            </div>
          ))}
        </div>

        {view.nothingToDo ? (
          <p className="ruleline" data-legibility="helper">
            {view.nothingToDo}
          </p>
        ) : null}

        <div className="navbar">
          {view.actionLabel ? (
            <button
              type="button"
              className="btn btn--primary"
              data-testid="import-confirm"
              disabled={chosen.size === 0}
              onClick={() => bring(stage.bundle)}
            >
              {view.actionLabel}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setStage({ at: 'idle' })}
          >
            Cancel
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {hidden}
      <button type="button" className="btn btn--ghost" data-testid="import-open" onClick={pick}>
        Open a backup file
      </button>
    </Card>
  );
}
